import { useEffect, useMemo, useState } from 'react'
import { fetchMetrics, type MetricVariant } from '../lib/api'
import { aud, mm, grams, metalLabel, METAL_LABELS } from '../lib/format'
import { Card, SectionTitle, EmptyState, TierBadge, EstFlag } from '../components/ui'
import { toCSV, downloadCSV, today } from '../lib/csv'

type SortKey = 'competitor_name' | 'price_aud' | 'price_per_gram' | 'melt_premium_aud' | 'band_width_mm'

export default function Compare() {
  const [rows, setRows] = useState<MetricVariant[] | null>(null)
  const [error, setError] = useState<string | null>(null)

  // Filters
  const [metal, setMetal] = useState('')
  const [custom, setCustom] = useState('')
  const [competitor, setCompetitor] = useState('')
  const [widthMin, setWidthMin] = useState('')
  const [widthMax, setWidthMax] = useState('')
  const [priceMax, setPriceMax] = useState('')
  const [sort, setSort] = useState<{ key: SortKey; dir: 1 | -1 }>({ key: 'price_aud', dir: 1 })

  useEffect(() => {
    fetchMetrics({ limit: '2000' }).then((r) => setRows(r.variants)).catch((e) => setError(String(e)))
  }, [])

  const competitors = useMemo(
    () => [...new Set((rows ?? []).map((r) => r.competitor_name))].sort(),
    [rows],
  )

  const filtered = useMemo(() => {
    if (!rows) return []
    return rows.filter((r) => {
      if (metal && r.metal !== metal) return false
      if (custom && String(r.is_custom) !== custom) return false
      if (competitor && r.competitor_name !== competitor) return false
      if (widthMin && (r.band_width_mm ?? -1) < parseFloat(widthMin)) return false
      if (widthMax && (r.band_width_mm ?? 1e9) > parseFloat(widthMax)) return false
      if (priceMax && (r.price_aud ?? 1e12) > parseFloat(priceMax)) return false
      return true
    })
  }, [rows, metal, custom, competitor, widthMin, widthMax, priceMax])

  const sorted = useMemo(() => {
    const val = (r: MetricVariant): number | string => {
      if (sort.key === 'competitor_name') return r.competitor_name
      if (sort.key === 'price_per_gram') return r.metrics.price_per_gram ?? Infinity * sort.dir
      if (sort.key === 'melt_premium_aud') return r.metrics.melt_premium_aud ?? -Infinity * sort.dir
      return (r[sort.key] as number | null) ?? Infinity * sort.dir
    }
    return [...filtered].sort((a, b) => {
      const av = val(a), bv = val(b)
      if (typeof av === 'string') return sort.dir * av.localeCompare(bv as string)
      return sort.dir * (av - (bv as number))
    })
  }, [filtered, sort])

  const th = (key: SortKey, label: string, align = 'left') => (
    <th
      className={`cursor-pointer select-none px-3 py-2 text-${align} font-body text-[11px] uppercase tracking-wide text-oxblood/60 hover:text-oxblood`}
      onClick={() => setSort((s) => (s.key === key ? { key, dir: (s.dir * -1) as 1 | -1 } : { key, dir: 1 }))}
    >
      {label}{sort.key === key ? (sort.dir === 1 ? ' ↑' : ' ↓') : ''}
    </th>
  )

  const exportCsv = () => {
    const headers = [
      'Competitor', 'Tier', 'Type', 'Metal', 'Design profile', 'Width mm', 'Finger size',
      'Weight g', 'Weight estimated', 'Price AUD', 'Price type', '$/gram', 'Melt premium AUD',
      'Melt premium %', 'Source URL',
    ]
    const data = sorted.map((r) => [
      r.competitor_name, r.positioning_tier, r.is_custom ? 'custom' : 'ready-made',
      r.metal, r.design_profile, r.band_width_mm, r.finger_size,
      r.metrics.weight_grams, r.metrics.weight_is_estimated ? 'estimated' : 'published',
      r.price_aud, r.price_type, r.metrics.price_per_gram, r.metrics.melt_premium_aud,
      r.metrics.melt_premium_pct, r.source_url,
    ])
    downloadCSV(`daniella-wedding-bands-${today()}.csv`, toCSV(headers, data))
  }

  if (error) return <EmptyState title="Couldn't load variants" hint={error} />

  return (
    <div className="space-y-6">
      <MatchMySpec rows={rows ?? []} />

      <section>
        <div className="mb-3 flex items-baseline justify-between">
          <SectionTitle note={`${filtered.length} of ${rows?.length ?? 0} variants`}>Comparison explorer</SectionTitle>
          <button onClick={exportCsv} disabled={!sorted.length}
            className="rounded-md border border-oxblood/25 px-4 py-1.5 font-body text-sm text-oxblood hover:bg-oxblood/5 disabled:opacity-40">
            Export CSV
          </button>
        </div>

        {/* Filter panel */}
        <Card className="mb-4 p-4">
          <div className="grid grid-cols-2 gap-3 md:grid-cols-6">
            <Select label="Metal" value={metal} onChange={setMetal} options={Object.entries(METAL_LABELS).map(([v, l]) => ({ v, l }))} />
            <Select label="Type" value={custom} onChange={setCustom} options={[{ v: '0', l: 'Ready-made' }, { v: '1', l: 'Custom' }]} />
            <Select label="Competitor" value={competitor} onChange={setCompetitor} options={competitors.map((c) => ({ v: c, l: c }))} />
            <Field label="Width min" value={widthMin} onChange={setWidthMin} placeholder="mm" />
            <Field label="Width max" value={widthMax} onChange={setWidthMax} placeholder="mm" />
            <Field label="Price max" value={priceMax} onChange={setPriceMax} placeholder="AUD" />
          </div>
        </Card>

        {sorted.length === 0 ? (
          <EmptyState title={rows?.length ? 'No variants match these filters' : 'No variant data yet'}
            hint={rows?.length ? 'Loosen the filters above.' : 'Run a scrape to populate the explorer.'} />
        ) : (
          <Card className="overflow-x-auto">
            <table className="w-full min-w-[820px] border-collapse">
              <thead className="border-b border-oxblood/10 bg-blush/20">
                <tr>
                  {th('competitor_name', 'Competitor')}
                  <th className="px-3 py-2 text-left font-body text-[11px] uppercase tracking-wide text-oxblood/60">Metal</th>
                  {th('band_width_mm', 'Width', 'right')}
                  <th className="px-3 py-2 text-right font-body text-[11px] uppercase tracking-wide text-oxblood/60">Weight</th>
                  {th('price_aud', 'Price', 'right')}
                  {th('price_per_gram', '$/g', 'right')}
                  {th('melt_premium_aud', 'Melt premium', 'right')}
                </tr>
              </thead>
              <tbody className="font-body text-sm">
                {sorted.slice(0, 400).map((r) => (
                  <tr key={r.variant_id} className="border-b border-oxblood/5 hover:bg-blush/10">
                    <td className="px-3 py-2">
                      <span className="text-ink">{r.competitor_name}</span>
                      <span className="ml-2"><TierBadge tier={r.positioning_tier} /></span>
                      {r.is_custom ? <span className="ml-1 text-[11px] text-ink/40">custom</span> : null}
                    </td>
                    <td className="px-3 py-2 text-ink/80">{metalLabel(r.metal)}</td>
                    <td className="px-3 py-2 text-right tabular-nums text-ink/80">{mm(r.band_width_mm)}</td>
                    <td className="px-3 py-2 text-right tabular-nums text-ink/70">
                      {grams(r.metrics.weight_grams)}<EstFlag show={r.metrics.weight_is_estimated} />
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums text-ink">
                      {r.price_aud == null
                        ? <span className="rounded bg-oxblood/5 px-1.5 py-0.5 text-[11px] uppercase text-oxblood/50">{r.price_type}</span>
                        : aud(r.price_aud, { round: true })}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums text-ink/70">{r.metrics.price_per_gram != null ? aud(r.metrics.price_per_gram) : '—'}</td>
                    <td className="px-3 py-2 text-right tabular-nums text-ink/70">{aud(r.metrics.melt_premium_aud, { round: true })}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {sorted.length > 400 && <p className="px-3 py-2 font-body text-xs text-ink/40">Showing first 400 of {sorted.length}. Narrow the filters or export (Stage 7).</p>}
          </Card>
        )}
      </section>
    </div>
  )
}

// "Match my spec" — Ethan's tool for "X quoted me less". Enter a spec; get each
// competitor's nearest equivalent and the price delta vs the cheapest match.
function MatchMySpec({ rows }: { rows: MetricVariant[] }) {
  const [metal, setMetal] = useState('18ct yellow gold')
  const [width, setWidth] = useState('4')
  const [sizeUs, setSizeUs] = useState('10')
  const [run, setRun] = useState(false)

  const matches = useMemo(() => {
    if (!run) return []
    const w = parseFloat(width), s = parseFloat(sizeUs)
    const scored = rows
      .filter((r) => r.metal === metal && r.price_aud != null && r.band_width_mm != null)
      .map((r) => ({
        r,
        dist: Math.abs((r.band_width_mm ?? 0) - w) + (r.finger_size_us != null && !Number.isNaN(s) ? Math.abs(r.finger_size_us - s) * 0.3 : 0),
      }))
    // nearest per competitor
    const best = new Map<string, { r: MetricVariant; dist: number }>()
    for (const m of scored) {
      const cur = best.get(m.r.competitor_name)
      if (!cur || m.dist < cur.dist) best.set(m.r.competitor_name, m)
    }
    const list = [...best.values()].sort((a, b) => (a.r.price_aud! - b.r.price_aud!))
    const cheapest = list[0]?.r.price_aud ?? null
    return list.map((m) => ({ ...m, delta: cheapest != null ? m.r.price_aud! - cheapest : null }))
  }, [run, rows, metal, width, sizeUs])

  return (
    <Card className="border-gold/40 bg-gold/5 p-4">
      <SectionTitle note="every competitor's nearest equivalent + price delta">Match my spec</SectionTitle>
      <div className="flex flex-wrap items-end gap-3">
        <Select label="Metal" value={metal} onChange={(v) => { setMetal(v); setRun(false) }}
          options={Object.entries(METAL_LABELS).map(([v, l]) => ({ v, l }))} allowEmpty={false} />
        <Field label="Width (mm)" value={width} onChange={(v) => { setWidth(v); setRun(false) }} placeholder="4" />
        <Field label="Size (US)" value={sizeUs} onChange={(v) => { setSizeUs(v); setRun(false) }} placeholder="10" />
        <button
          onClick={() => setRun(true)}
          className="rounded-md bg-oxblood px-5 py-2 font-body text-sm text-offwhite hover:bg-oxblood/90"
        >
          Match
        </button>
      </div>

      {run && (
        matches.length === 0 ? (
          <p className="mt-3 font-body text-sm text-ink/50">No priced {metalLabel(metal)} bands to match yet — run a scrape first.</p>
        ) : (
          <div className="mt-4 overflow-x-auto">
            <table className="w-full min-w-[560px] border-collapse font-body text-sm">
              <thead>
                <tr className="border-b border-oxblood/10">
                  {['Competitor', 'Nearest band', 'Width', 'Price', 'Δ vs cheapest'].map((h, i) => (
                    <th key={h} className={`px-3 py-1.5 font-body text-[11px] uppercase tracking-wide text-oxblood/60 ${i > 1 ? 'text-right' : 'text-left'}`}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {matches.map(({ r, delta }) => (
                  <tr key={r.variant_id} className="border-b border-oxblood/5">
                    <td className="px-3 py-2 text-ink">{r.competitor_name}</td>
                    <td className="px-3 py-2 text-ink/60">{r.product_name ?? metalLabel(r.metal)}</td>
                    <td className="px-3 py-2 text-right tabular-nums text-ink/70">{mm(r.band_width_mm)}</td>
                    <td className="px-3 py-2 text-right tabular-nums text-ink">{aud(r.price_aud, { round: true })}</td>
                    <td className={`px-3 py-2 text-right tabular-nums ${delta === 0 ? 'text-emerald-700' : 'text-ink/60'}`}>
                      {delta === 0 ? 'cheapest' : `+${aud(delta, { round: true })}`}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      )}
    </Card>
  )
}

function Select({ label, value, onChange, options, allowEmpty = true }: {
  label: string; value: string; onChange: (v: string) => void
  options: { v: string; l: string }[]; allowEmpty?: boolean
}) {
  return (
    <label className="block">
      <span className="mb-1 block font-body text-[11px] uppercase tracking-wide text-oxblood/50">{label}</span>
      <select value={value} onChange={(e) => onChange(e.target.value)}
        className="w-full rounded-md border border-oxblood/15 bg-white px-2 py-1.5 font-body text-sm text-ink">
        {allowEmpty && <option value="">All</option>}
        {options.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
      </select>
    </label>
  )
}

function Field({ label, value, onChange, placeholder }: {
  label: string; value: string; onChange: (v: string) => void; placeholder?: string
}) {
  return (
    <label className="block">
      <span className="mb-1 block font-body text-[11px] uppercase tracking-wide text-oxblood/50">{label}</span>
      <input value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} inputMode="decimal"
        className="w-full rounded-md border border-oxblood/15 bg-white px-2 py-1.5 font-body text-sm text-ink placeholder:text-ink/30" />
    </label>
  )
}
